import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { LanguageSelector } from '../ui/LanguageSelector';
import { useAuthStore } from '../../stores/authStore';

export function PublicNav() {
  const { t } = useTranslation('landing');
  const { user } = useAuthStore();
  const [isSolutionsOpen, setIsSolutionsOpen] = useState(false);

  return (
    <nav className="border-b border-gray-100 sticky top-0 bg-white/95 backdrop-blur-sm z-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-2">
            <svg
              className="w-8 h-8 text-blue-600"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
              />
            </svg>
            <span className="text-xl font-bold text-gray-900">xtFORM</span>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center gap-6">
            <a
              href="/#features"
              className="text-gray-600 hover:text-gray-900 text-sm font-medium"
            >
              {t('nav.features', 'Features')}
            </a>
            <a
              href="/#live-quiz"
              className="text-gray-600 hover:text-gray-900 text-sm font-medium"
            >
              {t('nav.liveQuiz', 'Live Quiz')}
            </a>

            {/* Solutions Dropdown */}
            <div
              className="relative group"
              onMouseEnter={() => setIsSolutionsOpen(true)}
              onMouseLeave={() => setIsSolutionsOpen(false)}
            >
              <button className="text-gray-600 hover:text-gray-900 text-sm font-medium flex items-center gap-1">
                {t('nav.solutions', 'Solutions')}
                <svg
                  className="w-4 h-4"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M19 9l-7 7-7-7"
                  />
                </svg>
              </button>
              <div className={`absolute top-full left-0 mt-2 w-48 bg-white rounded-lg shadow-lg border border-gray-100 transition-all z-50 ${
                isSolutionsOpen ? 'opacity-100 visible' : 'opacity-0 invisible'
              }`}>
                <Link
                  className="block px-4 py-3 text-sm text-gray-700 hover:bg-gray-50 rounded-t-lg"
                  to="/education"
                >
                  <div className="font-medium">{t('nav.education', 'Education')}</div>
                  <div className="text-xs text-gray-500">{t('nav.educationDesc', 'For teachers & schools')}</div>
                </Link>
                <Link
                  className="block px-4 py-3 text-sm text-gray-700 hover:bg-gray-50"
                  to="/business"
                >
                  <div className="font-medium">{t('nav.business', 'Business')}</div>
                  <div className="text-xs text-gray-500">{t('nav.businessDesc', 'For companies & teams')}</div>
                </Link>
                <Link
                  className="block px-4 py-3 text-sm text-gray-700 hover:bg-gray-50 rounded-b-lg"
                  to="/events"
                >
                  <div className="font-medium">{t('nav.events', 'Events')}</div>
                  <div className="text-xs text-gray-500">{t('nav.eventsDesc', 'For conferences & meetups')}</div>
                </Link>
              </div>
            </div>

            <Link
              className="text-gray-600 hover:text-gray-900 text-sm font-medium"
              to="/blog"
            >
              {t('nav.blog', 'Blog')}
            </Link>
            <a
              href="/#pricing"
              className="text-gray-600 hover:text-gray-900 text-sm font-medium"
            >
              {t('nav.pricing', 'Pricing')}
            </a>
          </div>

          {/* Right Side Actions */}
          <div className="flex items-center gap-4">
            {/* Language Selector */}
            <div className="relative">
              <LanguageSelector variant="compact" />
            </div>

            {/* Auth Buttons */}
            {user ? (
              <Link
                className="px-4 py-2 bg-blue-600 text-white rounded-lg font-medium text-sm hover:bg-blue-700 transition-colors"
                to="/formspace"
              >
                {t('nav.dashboard', 'Go to Dashboard')}
              </Link>
            ) : (
              <>
                <Link
                  className="text-gray-600 hover:text-gray-900 text-sm font-medium hidden sm:block"
                  to="/login"
                >
                  {t('nav.signIn', 'Sign In')}
                </Link>
                <Link
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg font-medium text-sm hover:bg-blue-700 transition-colors"
                  to="/register"
                >
                  {t('nav.getStarted', 'Get Started Free')}
                </Link>
              </>
            )}
          </div>
        </div>
      </div>
    </nav>
  );
}
